import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOpenTabs, OpenTab, deriveLabel } from '../context/OpenTabsContext';
import OpenTabItem from './OpenTabItem';
import { useTranslation } from 'react-i18next';
import { AppWindow } from 'lucide-react';

const OpenTabsBar: React.FC = () => {
  const { tabs, closeTab } = useOpenTabs();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Reading i18n.language causes this component to re-render on language switch
  // (same technique used in ShortcutNavBar lines ~29-32).
  const currentLanguage = i18n.language;

  if (tabs.length === 0) return null;

  const handleNavigate = (tab: OpenTab) => {
    navigate(tab.path, { state: tab.state });
  };

  const handleClose = (tab: OpenTab) => {
    const currentFullPath = `${location.pathname}${location.search}`;
    const wasActive = currentFullPath === tab.path;
    closeTab(tab.id);

    if (wasActive) {
      const remaining = tabs.filter((t) => t.id !== tab.id);
      if (remaining.length > 0) {
        const idx = tabs.findIndex((t) => t.id === tab.id);
        const target = remaining[Math.max(0, idx - 1)] ?? remaining[0];
        navigate(target.path, { state: target.state });
      } else {
        navigate('/dashboard', { state: { skipOpenTab: true } });
      }
    }
  };

  return (
    <div
      role="tablist"
      aria-label={t('tabs.barLabel')}
      className="flex items-end gap-0.5 px-4 pt-1.5 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
    >
      {/* Bar label — non-interactive, display only */}
      <span className="flex items-center gap-1 px-2 text-xs font-medium text-gray-500 flex-shrink-0 select-none self-center">
        <AppWindow size={14} />
        {t('tabs.barLabel')}
      </span>

      {tabs.map((tab) => {
        // Compute label at render time so it re-translates on language switch.
        // Strip query string before passing to deriveLabel (it only handles pathnames).
        const pathname = tab.path.split('?')[0];
        const renderedLabel = tab.labelOverride || deriveLabel(pathname, tab.state, t);
        // Include currentLanguage in the key so React re-mounts the tab on locale change,
        // mirroring ShortcutNavBar's tabKey pattern (~line 190).
        const tabKey = `${tab.id}-${currentLanguage}`;
        return (
          <OpenTabItem
            key={tabKey}
            tab={{ ...tab, label: renderedLabel }}
            isActive={`${location.pathname}${location.search}` === tab.path}
            onNavigate={handleNavigate}
            onClose={handleClose}
          />
        );
      })}
    </div>
  );
};

export default OpenTabsBar;
